import { DeleteOutlined } from "@ant-design/icons";
import { Button, Checkbox, Col, Input, Row, Form, Tooltip, Select } from "antd";
import "./metadata-row.css";
import { MetadataRowItem } from "../../../../api/data-types";
import { useState } from "react";
import { text } from "stream/consumers";

interface MetadataRowProps {
  theme: string;
  metadataItem: MetadataRowItem;
  id: number;
  handleRemoveItem: (key: number) => void;
  handleTableChange: (key: number, metadataItem: MetadataRowItem) => void;
  isMassUpdate: boolean;
  metadataKeys: string[];
  editableMetadataKeys: string[];
  setMetadataKeys: (keys: string[]) => void;
}

interface FormData {
  key: string;
  value: string;
  indexed: boolean;
}

export const MetadataRow = ({
  theme,
  metadataItem,
  id,
  handleRemoveItem,
  handleTableChange,
  isMassUpdate,
  metadataKeys,
  editableMetadataKeys,
  setMetadataKeys,
}: MetadataRowProps) => {
  const [form] = Form.useForm<FormData>();

  const [metadataRowItem, setMetadataRowItem] =
    useState<MetadataRowItem>(metadataItem);

  const handleRemoveClick = () => {
    handleRemoveItem(id);
  };

  const filterOption = (
    input: string,
    option?: { label: string; value: string }
  ) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase());

  const onValuesChange = () => {
    const updatedMetadataItem: MetadataRowItem = {
      ...metadataRowItem,
      item: {
        key: form.getFieldValue("key"),
        value: form.getFieldValue("value"),
        isIndexed: isMassUpdate ? !form.getFieldValue("indexed") : true,
      },
    };
    setMetadataRowItem(updatedMetadataItem);
    handleTableChange(id, updatedMetadataItem);
  };

  const textColor = theme === "light" ? "#000000" : "#ffffff";
  const keyStyle : string = metadataItem.itemTemplate.required ? "key-form-item-required" : "key-form-item";
  return (
    <Form
      style={{color: textColor}}
      form={form}
      initialValues={{
        key: metadataRowItem.item.key,
        value: metadataRowItem.item.value,
        indexed: !metadataRowItem.item.isIndexed,
      }}
    >
      <Row align="middle" className="metadata-row">
        <Col
          span={9}
          style={{ padding: "0px", paddingTop: "0px", paddingBottom: "0px" }}
        >
          <Form.Item<FormData> name="key" className={keyStyle}> 
            <Select
              showSearch
              style={{color: textColor}}
              placeholder="Input key"
              options={
                editableMetadataKeys &&
                editableMetadataKeys.map((item) => ({ label: item, value: item })) 
              }
              optionFilterProp="children"
              filterOption={filterOption}
              onSearch={(e) => {
                if (e.length > 0 && !metadataKeys.includes(e)) {
                  setMetadataKeys([e, ...metadataKeys]);
                }
              }}
              disabled={!isMassUpdate && metadataItem.itemTemplate.fromConfiguration}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item<FormData> name="value" className="value-form-item">
            {metadataRowItem.item.isIndexed && (
              <Input
                style={{color: textColor}}
              placeholder="Input value"
                onChange={onValuesChange}
                disabled={!isMassUpdate && metadataItem.itemTemplate.readOnly}
              ></Input>
            )}
          </Form.Item>
        </Col>
        {isMassUpdate && (
          <Col>
            <Form.Item<FormData> name="indexed" valuePropName="checked">
              <Checkbox onChange={onValuesChange}></Checkbox>
            </Form.Item>
          </Col>
        )}
        <Col>
          <Form.Item name="delete" className="delete-form-icon">
            {(!metadataItem.itemTemplate.required || isMassUpdate) && (
              <Button
                className="delete-icon-options"
                icon={<DeleteOutlined className="delete-icon" />}
                shape="circle"
                style={{ border: "none" }}
                size="large"
                onClick={handleRemoveClick}
              ></Button>
            )}
            {metadataItem.itemTemplate.required && !isMassUpdate && (
              <div style={{ height: "50px" }}></div>
            )}
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
};
